import React, { FC, useState, useEffect, useContext, useCallback } from 'react';

import OrderDetails from 'featureLib/OrderDetails';
import { CONTACT_DETAILS_FORM_CONFIG, DEFAULT_FORM_CONFIG, productOptions, typeOfOrderOptions } from 'featureLib/OrderDetails/formConfig';
import { RADIO_OPTIONS as siteTypeOptions, SINGLE_SITE, ERROR_TOAST } from 'home/view/manage-feasibility/constants';
import { IFORM, IFORM_ELEMENT } from 'featureLib/FormBuilder';
import { getDefaultValue, getFormValue, getSiteOptionsAndValue, convertLeadToOrderDetailsFormData, convertLsiToOrderDetailsFormData } from './form.control';
import { createNewLeadHandler } from 'tokenGeneration/response.handler';
import { prepareCreateLeadPayload } from './request.mapper';
import { AppContext } from 'App';
import { getCurrentLead } from 'tokenGeneration/lead.util';
import { handleError, findParamFromUrl, isSingleUpgradeOrShiftingJourney, setInSessionStorage, getFromSessionStorage } from 'common/utils/app.utils';
import { PRODUCT_ALIAS_MAP, CUSTOMER_SEGMENT, TYPEOF_ORDER, getProductType, LSI_ERRORS, STORAGE_KEY, JOURNEY_TYPE, isShiftingJourney } from 'common/constants/app.constants';
import { getLsiDetails } from 'home/view/upgrade-order/services';
import CircuitDetails from './CircuitDetails';
import MainLoader from 'common/components/loader/loader.component';
import { MODULE_ID } from 'home/common/constants/constants';
import { SESSION_STORAGE } from 'home/view/upgrade-order/constants';
import LoiPopup from './LoiPopup';

interface Props {
  onFormLoad: (formData: any) => void;
  onLeadSuccessCb: (formData: any) => void;
}

export const OrderDetailsAccordion: FC<Props> = ({ onFormLoad, onLeadSuccessCb }) => {
  const [orderFormState, setOrderFormState] = useState<any>(() => DEFAULT_FORM_CONFIG);
  const [contactFormState, setContactFormState] = useState<any>(() => CONTACT_DETAILS_FORM_CONFIG);
  const [loader, setLoader] = useState<boolean>(false);
  const [lsiLoading, setLsiLoading] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [lsiData, setLsiData] = useState<any>(null);
  const [lsiError, setLsiError] = useState<string>('');
  const [renderCircuitAccordion, setRenderCircuitAccordion] = useState<boolean>(false);
  const [showEditBtn, setShowEditBtn] = useState<boolean>(false);
  const [showLoiPopup, setShowLoiPopup] = useState<boolean>(false);

  const { selectedRadio, setToast, setSelectedRadio } = useContext<any>(AppContext);

  const getOptionsForTypeOfOrder = (currentProduct: { displayName: string; key: string }) => {
    const options = typeOfOrderOptions.filter((orderType) => orderType.products.includes(currentProduct.key));
    return options;
  };

  const getFormData = (defaultData?: any) => {
    if (defaultData && (isSingleUpgradeOrShiftingJourney() || isShiftingJourney() || isFormSelectedForSingleUpgradeOrShifting())) {
      return defaultData && convertLsiToOrderDetailsFormData(defaultData);
    }

    return convertLeadToOrderDetailsFormData(getCurrentLead() ?? {});
  };

  const getFormForOrder = (disableAll: boolean, defaultData?: any) => {
    const savedOrderValue = getFormData(defaultData)?.orderForm ?? {};
    const defaultValue = {
      selectedRadio
    };

    const defaultOrderFormState = getDefaultValue(DEFAULT_FORM_CONFIG, { ...defaultValue, ...savedOrderValue }, disableAll);

    const updatedTypeOfOrder = {
      ...defaultOrderFormState['typeOfOrder'],
      element: { ...defaultOrderFormState['typeOfOrder']?.element, props: { options: savedOrderValue?.orderTypeOptions } }
    };
    const updatedSiteType = {
      ...defaultOrderFormState['siteType'],
      element: { ...defaultOrderFormState['siteType']?.element, props: { options: savedOrderValue?.siteListOptions } }
    };
    const updatedLoiAvailable = {
      ...defaultOrderFormState?.loiAvailable,
      hide: defaultOrderFormState?.typeOfOrder?.value?.key !== TYPEOF_ORDER.NEW_PRE_ORDER && defaultOrderFormState?.typeOfOrder?.value?.key !== TYPEOF_ORDER.UPGRADE_PRE_ORDER
    };

    return { ...defaultOrderFormState, typeOfOrder: updatedTypeOfOrder, siteType: updatedSiteType, loiAvailable: updatedLoiAvailable };
  };

  const getFormForContact = (disableAll: boolean, defaultData?: any) => {
    const savedContactForm = getFormData(defaultData)?.contactForm ?? {};
    const defaultContactForm = getDefaultValue(CONTACT_DETAILS_FORM_CONFIG, savedContactForm ?? {}, disableAll);

    return defaultContactForm;
  };

  const getFormState = (disableAll: boolean, defaultData?: any) => {
    const orderForm = getFormForOrder(disableAll, defaultData);
    const contactForm = getFormForContact(disableAll, defaultData);

    return { orderForm, contactForm };
  };

  const setOrderForm = (disableAll: boolean) => {
    const orderFormCopy = Object.assign({}, orderFormState);
    Object.entries(orderFormCopy).forEach(([_key, value]) => {
      (value as any)['disabled'] = disableAll;
    });
    setOrderFormState(orderFormCopy);
  };

  const isFormSelectedForSingleUpgradeOrShifting = () => {
    const orderType = orderFormState?.typeOfOrder?.value?.key === TYPEOF_ORDER.UPGRADE || orderFormState?.typeOfOrder?.value?.key === TYPEOF_ORDER.SHIFTING;
    const siteType = orderFormState?.siteType?.value?.id === SINGLE_SITE;
    const isSingleUpgradeSelected = siteType && orderType;

    return isSingleUpgradeSelected;
  };

  const handleCreateLead = () => {
    const formValue = { ...orderFormState, ...contactFormState };
    setLoader(true);
    const payload = prepareCreateLeadPayload(formValue);
    const params = { serviceType: payload.product };
    createNewLeadHandler({ payload, params }).then(
      (resp) => {
        const { orderForm, contactForm } = getFormState(true);
        setLoader(false);
        setOrderFormState(orderForm);
        setContactFormState(contactForm);

        onLeadSuccessCb(getFormValue(formValue));
      },
      (error) => {
        setLoader(false);
        handleError(error);
      }
    );
  };

  const onSubmit = () => {
    setIsEditing(false);

    if (isFormSelectedForSingleUpgradeOrShifting()) {
      setOrderForm(true);
      setContactForm(true);
      setShowEditBtn(true);
      setRenderCircuitAccordion(true);
      setInSessionStorage(MODULE_ID, STORAGE_KEY.JOURNEY_TYPE, JOURNEY_TYPE.SINGLE_UPGRADE_SHIFTING);
      setSelectedRadio(siteTypeOptions.find((item: { id: string }) => item.id === SINGLE_SITE));
      return false;
    }

    if (isSingleUpgradeOrShiftingJourney() || isShiftingJourney()) {
      setInSessionStorage(MODULE_ID, STORAGE_KEY.JOURNEY_TYPE, JOURNEY_TYPE.SINGLE_UPGRADE_SHIFTING);
      setSelectedRadio(siteTypeOptions.find((item: { id: string }) => item.id === SINGLE_SITE));
      setContactForm(true);
    } else {
      const formValue = { ...orderFormState, ...contactFormState };
      const isNewPreOrder = formValue.typeOfOrder.value?.key === TYPEOF_ORDER.NEW_PRE_ORDER;
      const isUpgradePreOrder = formValue.typeOfOrder.value?.key === TYPEOF_ORDER.UPGRADE_PRE_ORDER;
      const isLoiAvailable = formValue.loiAvailable.value;

      if (isNewPreOrder || isUpgradePreOrder) {
        if (isLoiAvailable) {
          handleCreateLead();
        } else {
          setShowLoiPopup(true);
        }
      } else {
        handleCreateLead();
      }
    }
  };

  const handleLoiPopupProceedClick = () => {
    setShowLoiPopup(false);
    handleCreateLead();
  };

  const setFormConfigForSiteType = (form: IFORM, value: any) => {
    const isSMBCustomer = form?.selectedAccount?.value?.customerSegment === CUSTOMER_SEGMENT.SMB;
    const product = form['productType'].value?.key;
    const selectedSiteType = form?.siteType?.value?.id;
    const { siteListOptions, siteType } = getSiteOptionsAndValue(value, selectedSiteType, product);
    let updatedSiteType = {
      ...form['siteType'],
      value: siteType ?? form['siteType']?.value,
      element: { ...form['siteType']?.element, props: { options: siteListOptions } }
    };

    const updatedLoiAvailable = {
      ...form.loiAvailable,
      hide: value !== TYPEOF_ORDER.NEW_PRE_ORDER && value !== TYPEOF_ORDER.UPGRADE_PRE_ORDER
    };

    const isPreOrderUpgrade = value === 'UPGRADE_PRE_ORDER';
    const updatedProductOptions = isPreOrderUpgrade ? productOptions.filter((item) => item.key !== 'NLD' && item.key !== 'LL') : productOptions;
    const updatedProduct = {
      ...form.productType,
      element: {
        ...form.productType.element,
        props: {
          ...form.productType.element.props,
          options: updatedProductOptions
        }
      }
    };

    // filtered site types for SMB
    if (isSMBCustomer) {
      const updatedSiteListOptions = siteListOptions.filter((item: { id: string }) => item.id === SINGLE_SITE);
      updatedSiteType = {
        ...form['siteType'],
        value: updatedSiteListOptions?.[0],
        element: { ...form['siteType']?.element, props: { options: updatedSiteListOptions } }
      };
    }

    setTimeout(() => {
      setOrderFormState({ ...form, siteType: updatedSiteType, loiAvailable: updatedLoiAvailable, productType: updatedProduct });
    }, 300);
  };

  const handleOrderFormChange = ({ field, value, oldValue, newValue, form }: { field: string; value: any; oldValue: any; newValue: any; form: IFORM }) => {
    const updatedOrderNickName = { ...form['orderNickName'], errors: [], value: getAutoGeneratedOrderNickName({ form }) };
    if (field === 'selectedAccount' && oldValue?.partyId !== newValue?.partyId) {
      const isSMBCustomer = newValue?.customerSegment === CUSTOMER_SEGMENT.SMB;

      if (isSMBCustomer) {
        // filtered products for SMB
        const updatedProductOptions = productOptions.filter((item: { key: string }) => item.key === 'DEDICATED_INTERNET' || item.key === 'VPN');
        const selectedProduct = updatedProductOptions.find((item: { key: string }) => item.key === getProductType()) ?? updatedProductOptions?.[0];
        const updatedProductType = {
          ...form['productType'],
          value: selectedProduct,
          element: {
            ...form['productType'].element,
            props: { options: updatedProductOptions }
          }
        };

        // filtered type of order for SMB
        const updatedTypeOfOrder = {
          ...form['typeOfOrder'],
          value: typeOfOrderOptions?.[0],
          element: {
            ...form['typeOfOrder'].element,
            props: { options: typeOfOrderOptions }
          }
        };

        // filtered site types for SMB
        const updatedSiteListOptions = siteTypeOptions.filter((item: { id: string }) => item.id === SINGLE_SITE);
        const updatedSiteType = {
          ...form['siteType'],
          value: updatedSiteListOptions?.[0],
          element: { ...form['siteType']?.element, props: { options: updatedSiteListOptions } }
        };

        setOrderFormState({ ...form, orderNickName: updatedOrderNickName, productType: updatedProductType, typeOfOrder: updatedTypeOfOrder, siteType: updatedSiteType });
      } else {
        setOrderFormState({ ...form, orderNickName: updatedOrderNickName });
      }
    } else if (field === 'productType' && oldValue?.key !== newValue?.key) {
      let options = getOptionsForTypeOfOrder(value);
      const isSMBCustomer = form?.selectedAccount?.value?.customerSegment === CUSTOMER_SEGMENT.SMB;

      if (isSMBCustomer) {
        options = options.filter((item: { key: string }) => item.key === TYPEOF_ORDER.NEW);
      }
      const orderTypeValue = options[0];
      const updatedTypeOfOrder = { ...form['typeOfOrder'], value: orderTypeValue, element: { ...form['typeOfOrder']?.element, props: { options } } };
      setTimeout(() => {
        setFormConfigForSiteType({ ...form, typeOfOrder: updatedTypeOfOrder, orderNickName: updatedOrderNickName }, orderTypeValue?.key);
      }, 300);
    } else if (field === 'typeOfOrder' && oldValue?.key !== newValue?.key) {
      setFormConfigForSiteType({ ...form, orderNickName: updatedOrderNickName }, newValue?.key);
    }
  };

  const getAutoGeneratedOrderNickName = ({ form }: { form: IFORM }) => {
    const selectedAccount = form['selectedAccount']?.value?.partyName;
    const productType = form['productType']?.value?.key;
    const typeOfOrder = form['typeOfOrder']?.value?.displayName ?? form['typeOfOrder']?.value?.key;

    const selectedAccountName = selectedAccount?.substr(0, 4)?.toUpperCase();
    const productTypeName = PRODUCT_ALIAS_MAP[productType as keyof typeof PRODUCT_ALIAS_MAP];
    const typeOfOrderName = typeOfOrder;

    const autoGeneratedOrderNickName = selectedAccountName.concat(' ', productTypeName).concat(' ', typeOfOrderName);

    return autoGeneratedOrderNickName;
  };

  const onSubmitOrder = useCallback((form: { isValid: boolean; values: IFORM }) => {
    setOrderFormState(form?.values);
  }, []);

  const onSubmitContact = useCallback((form: { isValid: boolean; values: IFORM }) => {
    setContactFormState(form?.values);
  }, []);

  const onOrderFieldChange = useCallback(
    (
      field: string,
      value: any,
      initialState: any,
      { setForm, form, disableAll, oldValue, newValue }: { setForm: Function; form: IFORM; disableAll: Function; oldValue: any; newValue: any }
    ) => {
      handleOrderFormChange({ form, field, value, oldValue, newValue });
    },
    []
  );

  const onContactFieldChange = useCallback((field: string, value: any, empty: any, { setForm, form, disableAll }: { setForm: Function; form: IFORM; disableAll: Function }) => {},
  []);

  const getDisableBtn = useCallback(() => {
    if (getCurrentLead() && !isShiftingJourney()) {
      return true;
    }

    const disableSubmit = !orderFormState['selectedAccount']?.value?.partyId && !orderFormState['selectedAccount']?.value?.partyName;

    return loader || disableSubmit;
  }, [orderFormState, loader]);

  const updateOrderFormWithLsi = (lsiData: any) => {
    setLsiData(lsiData);

    // update order details form
    if (isSingleUpgradeOrShiftingJourney() || isShiftingJourney()) {
      const { orderForm, contactForm } = getFormState(true, lsiData);
      setOrderFormState(orderForm);
      setContactFormState(contactForm);
      onFormLoad?.(getFormValue({ ...orderForm, ...contactForm }));
      setInSessionStorage(MODULE_ID, STORAGE_KEY.JOURNEY_TYPE, JOURNEY_TYPE.SINGLE_UPGRADE_SHIFTING);
    }
    // else {
    //   onFormLoad?.(siteTypeOptions.find((item:any) => item?.id === SINGLE_SITE));
    // }
  };

  const fetchLsiDetails = async (lsiId?: string) => {
    try {
      setLsiLoading(true);

      const lsi = lsiId ?? findParamFromUrl('lsi', false) ?? getFromSessionStorage(SESSION_STORAGE.MODULE_ID, SESSION_STORAGE.LSI_ID);

      const { res, err } = await getLsiDetails(lsi);
      const result = await (res && res.data && res.data.result);

      if (err) {
        const errorMsg = (err && err.response && err.response.data && err.response.data.errorMsg) || LSI_ERRORS.DEFAULT_MSG;
        setLsiLoading(false);
        setLsiData({
          lsiId: lsi,
          installationAddress: { line1: null, city: null, state: null, pincode: null }
        });
        // handleError(err);
        setLsiError(errorMsg);
        throw new Error(errorMsg);
      }

      if (result) {
        // fetching new lsi details
        if (lsiId) {
          let errorMsg = '';

          const {
            product: newProductType,
            customer: { partyId: newPartyId }
          } = result;

          const existingPartyName = orderFormState['selectedAccount']?.value?.partyName;
          const existingPartyId = orderFormState['selectedAccount']?.value?.partyId;
          const existingProductType = orderFormState['productType']?.value?.key;

          if (newPartyId !== existingPartyId) {
            errorMsg = LSI_ERRORS.PARTY_MISMATCH + (existingPartyName ? ` ${existingPartyName}` : '');
          }

          if (newProductType !== existingProductType) {
            errorMsg = LSI_ERRORS.PRODUCT_MISMATCH + (existingProductType ? ` ${existingProductType}` : '');
          }

          if (errorMsg) {
            setLsiError(errorMsg);
            setLsiData(result);
            // setToast(true, errorMsg, ERROR_TOAST);
          } else {
            setLsiError('');
            // setToast(false, '', '');
            updateOrderFormWithLsi(result);
          }
        } else {
          setLsiError('');
          // setToast(false, '', '');
          updateOrderFormWithLsi(result);
        }

        setLsiLoading(false);
      }
    } catch (e) {
      console?.error(e);
    }
  };

  const setContactForm = (disableAll: boolean) => {
    const contactFormCopy = Object.assign({}, contactFormState);
    Object.entries(contactFormCopy).forEach(([_key, value]) => {
      (value as any)['disabled'] = disableAll;
    });
    setContactFormState(contactFormCopy);
  };

  const handleEditClick = () => {
    setShowEditBtn(false);
    setIsEditing(true);
    setContactForm(false);
  };

  useEffect(() => {
    if (isSingleUpgradeOrShiftingJourney() || isShiftingJourney()) {
      fetchLsiDetails();
    } else if (isFormSelectedForSingleUpgradeOrShifting()) {
      setOrderForm(true);
      setContactForm(true);
      setShowEditBtn(true);
      setRenderCircuitAccordion(true);
    } else {
      const { orderForm, contactForm } = getFormState(getCurrentLead() ? true : false);
      setOrderFormState(orderForm);
      setContactFormState(contactForm);
      onFormLoad?.(getFormValue({ ...orderForm, ...contactForm }));
    }
  }, [getCurrentLead()?.leadId]);

  if ((isSingleUpgradeOrShiftingJourney() || isShiftingJourney() || isFormSelectedForSingleUpgradeOrShifting()) && loader) {
    return (
      <div className="container">
        <MainLoader />
      </div>
    );
  }

  return (
    <div className="order-details-accordion">
      {showLoiPopup && <LoiPopup setshowPopup={setShowLoiPopup} onProceedClick={handleLoiPopupProceedClick} />}
      <OrderDetails
        onSubmit={onSubmit}
        orderFormSchema={orderFormState}
        onOrderFieldChange={onOrderFieldChange}
        onSubmitOrder={onSubmitOrder}
        contactFormSchema={contactFormState}
        onContactFieldChange={onContactFieldChange}
        onSubmitContact={onSubmitContact}
        loader={loader}
        disableBtn={getDisableBtn()}
        onEdit={handleEditClick}
        submitBtnName={isSingleUpgradeOrShiftingJourney() ? 'Save Changes' : 'Next'}
        showEditBtn={showEditBtn}
      />
      {(isSingleUpgradeOrShiftingJourney() || renderCircuitAccordion) && (
        <CircuitDetails
          lsiData={lsiData}
          fetchLsiDetails={fetchLsiDetails}
          lsiError={lsiError}
          isEditing={isEditing}
          setLoader={setLoader}
          loader={loader}
          lsiLoading={lsiLoading}
          orderFormState={orderFormState}
          contactFormState={contactFormState}
        />
      )}
    </div>
  );
};

export default OrderDetailsAccordion;
